/**
 * useEffectivePermissions Hook
 * 
 * Provides effective permission state for global admins who can simulate
 * viewing the app as different roles/tenants. This hook differentiates between:
 * 
 * - isActualGlobalAdmin: True database global admin status (use for authorization/permission checks)
 * - shouldFilterByTenant: Whether data queries should filter by tenant (use for data fetching)
 * - shouldFilterByUser: Whether pipeline data should filter by user (when viewing as sales_rep)
 * - effectiveTenantId: The tenant ID to use for queries
 * - effectiveRole: The role being simulated (or actual role if not simulating)
 * 
 * CRITICAL: Use isActualGlobalAdmin for permission checks (showing admin UI, buttons, etc.)
 * CRITICAL: Use shouldFilterByTenant for data fetching queries
 */

import { useMemo } from 'react';
import { useTenant } from './useTenant';
import { useRoleView } from '@/context/RoleViewContext';
import { debugLog } from '@/lib/debug';
import type { Database } from '@/integrations/supabase/types';

type AppRole = Database['public']['Enums']['app_role'];

export interface EffectivePermissions {
 /**
 * True if user is actually a global admin in the database.
 * USE THIS FOR: Permission checks, showing admin UI, authorization decisions.
 * DO NOT USE FOR: Data filtering in queries.
 */
 isActualGlobalAdmin: boolean;

 /**
 * True when data queries should filter by tenant.
 * This is true when:
 * - User is NOT a global admin, OR
 * - User IS a global admin but is viewing as a non-global-admin role
 * 
 * USE THIS FOR: Adding .eq('tenant_id', tenantId) to data queries.
 */
 shouldFilterByTenant: boolean;

 /**
 * True when pipeline/personal data should filter by user ID.
 * This is true when viewing as sales_rep role.
 * 
 * USE THIS FOR: Adding .eq('team_lead_id', userId) to pipeline queries.
 */
 shouldFilterByUser: boolean;

 /**
 * The tenant ID to use for filtered queries.
 * Returns the current tenant's ID (respects tenant switching for global admins).
 */
 effectiveTenantId: string | null;

 /**
 * The role being viewed as (simulated role for global admins, actual role otherwise).
 */
 effectiveRole: AppRole | null;

 /**
 * The user's actual database role (not the simulated one).
 */
 actualRole: AppRole | null;

 /**
 * Whether the user is currently simulating a different role than their actual role.
 */
 isSimulatingRole: boolean;
}

/**
 * Hook that provides effective permissions for data filtering vs authorization.
 * 
 * @example
 * ```tsx
 * const { shouldFilterByTenant, effectiveTenantId, isActualGlobalAdmin } = useEffectivePermissions();
 * 
 * // For data queries - filter by tenant when needed
 * let query = supabase.from('users').select('*');
 * if (shouldFilterByTenant && effectiveTenantId) {
 * query = query.eq('tenant_id', effectiveTenantId);
 * }
 * 
 * // For permission checks - use actual admin status
 * if (isActualGlobalAdmin) {
 * // Show admin-only UI controls
 * }
 * ```
 */
export function useEffectivePermissions(): EffectivePermissions {
 const { tenant, isGlobalAdmin } = useTenant();
 const { currentRole, viewingAsRole } = useRoleView();

 return useMemo(() => {
 // The actual global admin status from database
 const isActualGlobalAdmin = isGlobalAdmin;

 // The role being viewed as (simulated or actual)
 const effectiveRole = (viewingAsRole || currentRole) as AppRole | null;

 // The user's actual database role
 const actualRole = currentRole as AppRole | null;

 // Whether currently simulating a different role
 const isSimulatingRole = isActualGlobalAdmin && 
 viewingAsRole !== null && 
 viewingAsRole !== 'global_admin';

 /**
 * Should filter by tenant when:
 * 1. User is NOT a global admin (always filter), OR
 * 2. User IS a global admin but viewing as a non-global-admin role
 * 
 * When a global admin views as global_admin, they see all data.
 * When a global admin views as tenant_admin/manager/sales_rep, they see only that tenant's data.
 */
 const shouldFilterByTenant = !isActualGlobalAdmin || 
 (isActualGlobalAdmin && viewingAsRole !== null && viewingAsRole !== 'global_admin');

 /**
 * Should filter by user (for pipeline/personal data) when:
 * - Viewing as sales_rep role
 * 
 * This makes global admins see only their own pipeline items when viewing as sales_rep,
 * which will typically be empty (correct behavior for testing that role's view).
 */
 const shouldFilterByUser = effectiveRole === 'sales_rep';

 // The tenant ID to use for filtered queries
 const effectiveTenantId = tenant?.id || null;

 // Debug logging (controlled by DEBUG_ROLE_VIEW flag)
 debugLog('[EffectivePermissions] State:', {
 isActualGlobalAdmin,
 shouldFilterByTenant,
 shouldFilterByUser,
 effectiveTenantId,
 effectiveRole,
 actualRole,
 isSimulatingRole,
 viewingAsRole,
 tenantName: tenant?.name,
 });

 return {
 isActualGlobalAdmin,
 shouldFilterByTenant,
 shouldFilterByUser,
 effectiveTenantId,
 effectiveRole,
 actualRole,
 isSimulatingRole,
 };
 }, [isGlobalAdmin, tenant?.id, currentRole, viewingAsRole, tenant?.name]);
}
